// frontend/src/components/EdaAnalysis/EdaSeparability.tsx

import React from 'react';

interface Props {
  analysis: any;
}

const renderValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const renderPercent = (value: any): React.ReactNode => {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return `${(n * 100).toFixed(1)}%`;
};

// Renderiza um objeto aninhado como tabela (chave → valor).
// Usado para _anova e para qualquer valor que seja objeto.
const NestedTable: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const entries = Object.entries(data);
  if (entries.length === 0) return <span>—</span>;

  // Coleta todas as chaves de todos os objetos filhos (colunas dinâmicas).
  const childKeys = new Set<string>();
  for (const [, v] of entries) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      Object.keys(v).forEach((k) => childKeys.add(k));
    }
  }
  const columns = Array.from(childKeys);

  return (
    <table style={{
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '13px',
      color: '#ffffff',
      marginTop: '6px',
    }}>
      <thead>
        <tr style={{ borderBottom: '1px solid #1a4a7a' }}>
          <th style={{ textAlign: 'left', padding: '6px', color: '#8899aa', fontWeight: 500 }}>chave</th>
          {columns.map((c) => (
            <th key={c} style={{ textAlign: 'left', padding: '6px', color: '#8899aa', fontWeight: 500 }}>{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {entries.map(([k, v]) => (
          <tr key={k} style={{ borderBottom: '1px solid #16213e' }}>
            <td style={{ padding: '6px' }}>{k}</td>
            {columns.map((c) => (
              <td key={c} style={{ padding: '6px' }}>
                {v && typeof v === 'object' && !Array.isArray(v)
                  ? renderValue((v as any)[c])
                  : c === columns[0] ? renderValue(v) : '—'}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const EdaSeparability: React.FC<Props> = ({ analysis }) => {
  const separability = analysis?.separability;

  const separabilityEntries =
    separability && typeof separability === 'object' && !Array.isArray(separability)
      ? Object.entries(separability)
      : [];

  return (
    <div className="section-card">
      <h3>📈 Métricas de Separabilidade</h3>

      <div className="separability-grid">
        <div className="separability-item">
          <div className="value">{renderValue(analysis?.max_separability)}</div>
          <div className="label">Max Separability</div>
        </div>
        <div className="separability-item">
          <div className="value">{renderPercent(analysis?.baseline_auc)}</div>
          <div className="label">Baseline AUC</div>
        </div>
        <div className="separability-item">
          <div className="value">{renderPercent(analysis?.baseline_accuracy)}</div>
          <div className="label">Baseline Accuracy</div>
        </div>
      </div>

      {separabilityEntries.length === 0 ? (
        <div style={{ marginTop: '16px', fontSize: '14px', color: '#8899aa' }}>
          Sem separabilidade registrada.
        </div>
      ) : (
        <div style={{ marginTop: '16px' }}>
          <h4 style={{ color: '#8899aa', marginBottom: '8px' }}>Separabilidade por Feature</h4>
          {separabilityEntries.map(([key, value]) => (
            <div
              key={key}
              style={{
                padding: '8px 0',
                borderBottom: '1px solid rgba(26, 74, 122, 0.2)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#e0e0e0', fontSize: '14px', fontWeight: 600 }}>{key}</span>
                {value === null || value === undefined || typeof value !== 'object' || Array.isArray(value) ? (
                  <span style={{ color: '#4fc3f7', fontSize: '13px', fontWeight: 600 }}>
                    {renderValue(value)}
                  </span>
                ) : null}
              </div>

              {value && typeof value === 'object' && !Array.isArray(value) ? (
                <NestedTable data={value} />
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};